<script lang="ts" setup>
import 'gridstack/dist/gridstack.min.css'
import { GridStackWidget } from 'gridstack'
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { notify } from '@kyvg/vue3-notification'
import { v4 as uuidv4 } from 'uuid'
import { Switch } from '@headlessui/vue'

import useForm from '@/hooks/use-form'
import { ResourceResponse, uuid } from '@/types/general'
import { Dashboard, DashboardGridRef, DashboardWidget } from '@/types/dashboard'
import { useConfirm } from '@/hooks/use-confirm'
import { useDashboardStore } from '@/stores/dashboard-store'
import { useAuthStore } from '@/stores/auth-store'
import { MyButtonScheme } from '@/types/layout/my-button'
import { useMittListener } from '@/hooks/use-mitt-listener'

import WidgetBrowser from '@/components/dashboard/WidgetBrowser.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MyInput from '@/components/my-components/form/MyInput.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyCheckbox from '@/components/my-components/form/MyCheckbox.vue'
import DashboardGrid from '@/components/dashboard/DashboardGrid.vue'
import ShareDashboardModal from '@/components/dashboard/ShareDashboardModal.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const confirm = useConfirm()
const authStore = useAuthStore()
const dashboardStore = useDashboardStore()

const { data, submit, errors, reset } = useForm({})

const dashboardGrid = ref<DashboardGridRef>()
const loading = ref(false)
const shareDashboardModalOpen = ref(false)
const widgetBrowserOpen = ref(false)
const aspectFramePreview = ref(false)

async function resetFields() {
    dashboardStore.reloadCurrentDashboard()

    await nextTick()
    dashboardGrid.value?.refreshGrid()
    dashboardStore.setDashboardEditStatus(false)
    aspectFramePreview.value = false
}

function onGridChange(changeItems: GridStackWidget[]) {
    if (!changeItems) return
    changeItems.forEach((item) => {
        const widget = dashboardStore.currentDashboard!.widgets.find(
            (widget) => widget.id == item.id,
        )
        if (!widget) {
            return
        }

        widget.x = item.x ?? 0
        widget.y = item.y ?? 0
        widget.height = item.h ?? 1
        widget.width = item.w ?? 1
    })
}

async function reloadCurrentDashboard(dashboardSlug?: string) {
    await dashboardStore.fetchDashboards()
    await router.push({ name: 'dashboards.show', params: { slug: dashboardSlug } })
    dashboardStore.reloadCurrentDashboard()
}

async function save() {
    const response = await submit<ResourceResponse<Dashboard>>(
        'PUT',
        window.route('dashboards.update', {
            dashboard: dashboardStore.currentDashboard,
            data: data,
        }),
        {
            data: {
                name: dashboardStore.currentDashboard!.name,
                active: dashboardStore.currentDashboard!.active,
                private: dashboardStore.currentDashboard!.private,
                widgets: dashboardStore.currentDashboard!.widgets,
            },
        },
    )
    if (!response) return
    dashboardStore.setDashboardEditStatus(false)
    aspectFramePreview.value = false
    reloadCurrentDashboard(response.data.slug)
}

async function deleteDashboard() {
    try {
        loading.value = true

        await submit(
            'DELETE',
            window.route('dashboards.destroy', {
                dashboard: dashboardStore.currentDashboard,
            }),
        )

        dashboardStore.emptyCurrentDashboard()
        reset()
        dashboardStore.setDashboardEditStatus(false)
        aspectFramePreview.value = false

        router.replace({
            name: 'dashboard',
            query: route.query,
        })
    } finally {
        loading.value = false
    }
}

async function deleteConfirm() {
    try {
        await confirm(
            t('deleteEntityTitle', { entity: t('dashboard') }),
            t('deleteDashboardPrompt'),
            {
                confirmText: t('confirm'),
                cancelText: t('cancel'),
                confirmButtonScheme: MyButtonScheme.Danger,
            },
        )
        await deleteDashboard()
    } catch (e) {
        if (!e) return
        notify({ type: 'error', text: t('deleteDashboardError') })
        throw e
    }
}

function isDefaultDashboard(dashboardId: uuid) {
    return dashboardId === dashboardStore.defaultDashboardId
}

async function setDefaultDashboard(dashboard: Dashboard) {
    loading.value = true
    try {
        const response = await axios.put<uuid>(
            window.route('company.dashboards.favorite', {
                company: authStore.companyId,
                dashboard: dashboard,
            }),
        )
        if (response) {
            dashboardStore.setDefaultDashboard(response.data)
        }
    } finally {
        loading.value = false
    }
}

async function addWidget(widget: DashboardWidget) {
    if (!dashboardGrid.value) return
    widget.id = uuidv4()
    const newWidget = dashboardGrid.value!.addWidget(widget)
    dashboardStore.currentDashboard?.widgets.push(newWidget)
}

async function deleteWidget(widget: DashboardWidget) {
    if (!dashboardGrid.value) return

    dashboardStore.deleteWidget(widget.id)
    dashboardGrid.value!.refreshGrid()
}

function hasChanges(): boolean {
    return JSON.stringify(dashboardStore.currentDashboard) !== dashboardStore.initialDashboard
}

function beforeUnload(e: BeforeUnloadEvent) {
    if (hasChanges()) e.preventDefault()
}

async function confirmLeave() {
    if (!hasChanges()) return true

    try {
        await confirm(t('unsavedChanges'), t('unsavedDashboardChangesDescription'), {
            confirmText: t('yes'),
            cancelText: t('no'),
            confirmButtonScheme: MyButtonScheme.Warning,
        })
        dashboardStore.setDashboardEditStatus(false)
        return true
    } catch (_) {
        return false
    }
}

onMounted(() => {
    if (!dashboardStore.currentDashboard) return
    window.addEventListener('beforeunload', beforeUnload)
})

onBeforeUnmount(() => window.removeEventListener('beforeunload', beforeUnload))
onBeforeRouteLeave(confirmLeave)
onBeforeRouteUpdate(confirmLeave)

useMittListener('deleteWidget', deleteWidget)
</script>

<template>
    <div v-if="dashboardStore.currentDashboard" class="flex flex-col">
        <LoaderWrapper :visible="loading" />
        <div class="flex ml-auto mt-4 space-x-3">
            <MyButton
                :scheme="
                    isDefaultDashboard(dashboardStore.currentDashboard.id) ? 'success' : 'primary'
                "
                :plain="!isDefaultDashboard(dashboardStore.currentDashboard.id)"
                @click.stop="setDefaultDashboard(dashboardStore.currentDashboard)"
            >
                <mdi:star
                    class="text-base mr-2"
                    :class="{
                        '!text-yellow-400': isDefaultDashboard(dashboardStore.currentDashboard.id),
                    }"
                />
                {{
                    isDefaultDashboard(dashboardStore.currentDashboard.id)
                        ? t('unFavorite')
                        : t('favorite')
                }}
            </MyButton>
            <MyButton
                scheme="primary"
                plain
                @click="shareDashboardModalOpen = !shareDashboardModalOpen"
            >
                <mdi:share class="mr-2" />
                {{ t('share') }}
            </MyButton>
            <MyButton
                v-if="!dashboardStore.isEditing"
                plain
                scheme="primary"
                @click="dashboardStore.setDashboardEditStatus(true)"
            >
                <mdi:pencil class="mr-2" />
                {{ t('edit') }}
            </MyButton>
        </div>
        <div
            class="flex flex-col shadow-xl bg-primary-50 dark:bg-dark-500 rounded-md p-3 mb-3 mt-6 ml-auto w-full"
            :class="{
                '!bg-transparent !shadow-none !p-0': !dashboardStore.isEditing,
            }"
        >
            <MyForm
                v-if="dashboardStore.isEditing"
                class="flex flex-col"
                :errors="errors"
                @submit.prevent="save()"
            >
                <div class="flex w-full">
                    <div class="flex flex-col space-y-2">
                        <MyInput
                            v-model="dashboardStore.currentDashboard.name"
                            name="name"
                            :label="t('name')"
                        />
                        <div class="flex space-x-2">
                            <MyCheckbox
                                v-model="dashboardStore.currentDashboard.private"
                                :label="t('private')"
                                :checked="dashboardStore.currentDashboard.private"
                            />
                            <mdi:information v-tooltip="t('privateDashboardExplanation')" />
                        </div>
                    </div>
                    <MyButton
                        type="button"
                        size="small"
                        class="w-fit text-red-600 ml-auto"
                        @click.prevent="deleteConfirm()"
                    >
                        <mdi:trash-can class="mr-1" />
                        {{ t('deleteDashboard') }}
                    </MyButton>
                </div>

                <div class="flex flex-col items-center justify-between">
                    <div class="ml-auto space-x-3">
                        <MyButton
                            scheme="light"
                            type="button"
                            @click.prevent="resetFields()"
                            v-text="t('cancel')"
                        />
                        <MyButton scheme="primary" type="submit" v-text="t('save')" />
                    </div>
                </div>
            </MyForm>
        </div>
        <div class="flex justify-end mb-3 items-center gap-3">
            <div v-if="dashboardStore.isEditing" class="flex items-center gap-3">
                <Switch
                    v-model="aspectFramePreview"
                    :class="aspectFramePreview ? 'bg-primary-600' : 'bg-stone-300'"
                    class="relative inline-flex h-[28px] w-[64px] flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-opacity-75"
                >
                    <span class="sr-only" v-text="t('aspectFramePreview')" />

                    <span
                        :class="aspectFramePreview ? 'translate-x-9' : 'translate-x-0'"
                        aria-hidden="true"
                        class="pointer-events-none inline-block h-[24px] w-[24px] transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out"
                    />
                </Switch>
                <span v-text="t('aspectFramePreview')" />
            </div>
            <MyButton
                v-if="dashboardStore.isEditing"
                scheme="primary"
                @click="widgetBrowserOpen = true"
            >
                <mdi-plus-thick class="mr-1" />
                {{ t('addWidgets') }}
            </MyButton>
        </div>
        <div
            v-if="dashboardStore.currentDashboard.widgets.length === 0 && !dashboardStore.isEditing"
            class="text-center font-semibold text-xl mt-10 dark:text-primary-50 text-primary-700"
        >
            {{ t('noWidgets', { entity: dashboardStore.currentDashboard.name }) }}
        </div>

        <div
            class="relative"
            :class="{
                'border-dashed border-primary-300 dark:border-dark-300 border-2 rounded-md':
                    dashboardStore.isEditing && !aspectFramePreview,
            }"
        >
            <div
                v-if="dashboardStore.isEditing && aspectFramePreview"
                class="absolute w-full border-2 border-dashed border-blue-600 bg-blue-300 aspect-video pointer-events-none rounded-md bg-opacity-10"
            >
                <span
                    class="absolute top-0 left-0 z-20 py-1 px-2 rounded-md bg-blue-600 -translate-y-full text-white"
                    v-text="'16:9'"
                />
                <span
                    class="absolute bottom-0 right-0 z-20 py-1 px-2 rounded-md bg-blue-600 translate-y-full text-white"
                    v-text="'16:9'"
                />
            </div>

            <DashboardGrid
                ref="dashboardGrid"
                :widgets="dashboardStore.currentDashboard.widgets"
                @change="onGridChange"
            />
        </div>
    </div>
    <ShareDashboardModal
        v-model="shareDashboardModalOpen"
        @close="shareDashboardModalOpen = false"
    />
    <WidgetBrowser v-model="widgetBrowserOpen" @add-widget="addWidget" />
</template>
